import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TooltipItem,
  } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useWindowWidthAndHeight } from '../../../shared/hooks/customHooks';
import { useTranslation } from 'react-i18next';
import { convertNumberToString } from '../../../shared/utils/numbersUtils';
import i18next from 'i18next';
import { dimensions } from '../../../redux/states/chartState';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export type Props = {
  labels: string[]
  dataSets: any[],
  ariaLabel: string,
  containerDimensions: dimensions,
}

const LineChart: React.FC<Props> = ({
  labels,
  dataSets,
  ariaLabel,
  containerDimensions,
}) => {

  const [width] = useWindowWidthAndHeight();
  const {t,i18n} = useTranslation();
  const yScaleText = {
    id:'yScaleText',
    afterDraw(chart: any, args: any, options: any) {
      const { ctx, scales: { x, y } } = chart;
      ctx.save();
      ctx.font='14px Open sans:Bold';
      ctx.fillStyle='#2D363F';
      ctx.fillText(t('year_unit_plural', {postProcess: 'plural'}), 0, 10);
      ctx.restore();
    }
  };

  ChartJS.register(yScaleText);

  const optionsDesktop = {
    layout:{
      padding:{
        top: 0,
      }
    },

    responsive: true,
    aspectRatio: containerDimensions.width / containerDimensions.height,
    resizeDelay: 100,

    plugins: {
      legend: {
        display: true,
        labels: {
          boxHeight: 5,
          boxWidth: 35,
          usePointStyle: true,
          fullSize: true,
        },
        itemWidth: 4000 
      },

      tooltip:{
        displayColors: false,
        backgroundColor: "rgba(77,79,82,1)",
        titleFont: {
          size: 16
        },
        bodyFont: {
          size: 15,
          family: "sans-serif",
          weight: "normal"
        },
        footerFont: {
          size: 20
        },
        titleMarginBottom: 6,
        padding: 10,
        callbacks: {
          title: function(tooltipItem: TooltipItem<any>[]): string {
            return tooltipItem[0].label;
          },
          label: function(tooltipItem: TooltipItem<any>): string {
            return tooltipItem.dataset.label;
          },
          footer: function(tooltipItem: TooltipItem<any>[]): string {
            return (
              convertNumberToString(tooltipItem[0].raw as number, i18n, 1, 2)
              + " "
              + i18next.t('year_unit_interval', 
                {
                  postProcess: 'interval', 
                  count: tooltipItem[0].raw as number
                })
            );
          },
        }
      }
    }, 

    elements: {
      point:{
        radius: 4,
        hoverRadius: 7,
        borderColor: "rgb(255,255,0)",
        hitRadius: 10
      }
    },

    scales:{
      x: {
        grid:{
          borderDash:function(ctx:any){
            return ctx.tick.label!==""? [0,0] : [0,20]
          },
          color:"#00000029",
          drawTicks:false
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min:labels[0],
          max:labels[labels.length-1],
          callback: function(index:any) {
            const result = canDisplay(index)?labels[index]:''
            return result
          },
          color: "#2D363F",
          stepSize: 1,
          font: {
            size: 14,
            fontStyle: "bold",
          } 
        }
      },

      y: {
        ticks:{
          color:'#2D363F',
          callback: function(value:any,index:any) {
            return convertNumberToString(value, i18n, 1, 2)
          },
          font:{
            size:14,
            fontStyle: "bold",
          },
        },
        grid:{
          color:"#00000029",
          drawTicks:false
        },
      },
    }
  };

  const optionsMobile = {
    layout:{
      padding:{
        top: 0,
      }
    },

    responsive: true,
    aspectRatio: 2,
    resizeDelay: 100,

    plugins: {
      legend: {
        display: true,
        labels: {
          boxHeight: 5,
          boxWidth: 35,
          usePointStyle: true,
          fullSize: true,
        },
        itemWidth: 4000 
      },

      tooltip:{
        displayColors: false,
        backgroundColor: "rgba(77,79,82,1)",
        titleFont: {
          size: 16
        },
        bodyFont: {
          size: 15,
          family: "sans-serif",
          weight: "normal"
        },
        footerFont: {
          size: 20
        },
        titleMarginBottom: 6,
        padding: 10,
        callbacks: {
          title: function(tooltipItem: TooltipItem<any>[]): string {
            return tooltipItem[0].label;
          },
          label: function(tooltipItem: TooltipItem<any>): string {
            return tooltipItem.dataset.label;
          },
          footer: function(tooltipItem: TooltipItem<any>[]): string {
            return (
              convertNumberToString(tooltipItem[0].raw as number, i18n, 1, 2)
              + " "
              + i18next.t('year_unit_interval', {
                  postProcess: 'interval', 
                  count: tooltipItem[0].raw as number 
                })
            );
          },
        }
      }
    }, 

    elements: {
      point:{
        radius: 2,
        hoverRadius: 3,
        borderColor: "rgb(255,255,0)",
        hitRadius: 5
      }
    },

    scales:{
      x: {
        grid:{
          borderDash:function(ctx:any){
            return ctx.tick.label!==""? [0,0] : [0,20]
          },
          color:"#00000029",
          drawTicks:false
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min:labels[0],
          max:labels[labels.length-1],
          callback: function(index:any) {
            const result = canDisplay(index)?labels[index]:''
            return result
          },
          color: "#2D363F",
          stepSize: 1,
          font: {
            size: 14,
            fontStyle: "bold",
          } 
        }
      },

      y: {
        ticks:{
          color:'#2D363F',
          callback: function(value:any,index:any) {
            return convertNumberToString(value, i18n, 1, 2)
          },
          font:{
            size:14,
            fontStyle: "bold",
          },
        },
        grid:{
          color: "#00000029",
          drawTicks: false
        },
      },
    }
  };
    
  const canDisplay=(index:number)=>{
    const value:number = parseInt(labels[index])
    if(index===0 || index===labels.length-1){
      return true;
    }
    else if(width<450){
      if(index===0|| index===labels.length-1) return true
      else return false
    }
    else{
      let condition=0;
      if(width>1150){
        condition=10;
      }
      else{
        condition=5;
      }
      if(labels.length<=condition){
        return true;
      }

      else if(labels.length>condition && labels.length<=25){
        if(value%5===0){
          return true;
          }
      }
      else{
        if(value%10===0){
            return true;
        } 
      }
    }
  }

  const data = {
    labels,
    datasets: dataSets
  };

  const options = width > 1150 
    ? optionsDesktop
    : optionsMobile

  return (
    <Line
      role="img"
      aria-label={ariaLabel}
      options={options}
      data={data}
    />
  );
}

export default LineChart